import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import PageHeader from '@/components/ui/PageHeader/PageHeader';
import DataTable from '@/components/functional/DataTable/DataTable';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import QuestionStatusSelect from '@/components/admin/QuestionStatusSelect';
import QuestionContentEditor from '@/components/admin/QuestionContentEditor';
import QuestionMarkSchemeEditor from '@/components/admin/QuestionMarkSchemeEditor';
import UploadMarkSchemeButton from '@/components/admin/UploadMarkSchemeButton';
import PageNumbersEditor from '@/components/admin/PageNumbersEditor';
import PastPaperReadingInsertEditor from '@/components/admin/PastPaperReadingInsertEditor';
import { useAdminPastPaper, usePublishPastPaperQuestions } from '@/api/useAdminPastPaper';
import Routes from '@/router/Routes';
import { Database } from '@/_constants/database.types';

type QuestionRow = Database['public']['Tables']['questions']['Row'];

const EditPastPaperPage: FC = () => {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const { data: pastPaper, isLoading } = useAdminPastPaper(paperId);
  const { mutate: publishQuestions, isPending: isPublishing } = usePublishPastPaperQuestions();

  const handleBack = () => {
    navigate(Routes.ADMIN_PAST_PAPERS);
  };

  const handlePublish = () => {
    if (paperId) {
      publishQuestions(paperId, {
        onSuccess: () => {
          navigate(Routes.ADMIN_PAST_PAPERS);
        },
      });
    }
  };

  const columns: ColumnDef<QuestionRow>[] = useMemo(
    () => [
      {
        accessorKey: 'question_order',
        header: '#',
        enableSorting: false,
        size: 50,
        cell: (info) => (
          <span className="text-gray-900 font-medium">
            {((info.getValue() as number) ?? 0) + 1}
          </span>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        enableSorting: false,
        size: 100,
        cell: (info) => <span className="text-gray-600">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'content',
        header: 'Content',
        enableSorting: false,
        cell: ({ row }) => (
          <QuestionContentEditor question={row.original} pastPaperId={paperId!} />
        ),
      },
      {
        accessorKey: 'marks',
        header: 'Marks',
        enableSorting: false,
        size: 70,
        cell: (info) => <span className="text-gray-600">{info.getValue() as number}</span>,
      },
      {
        accessorKey: 'image_page_numbers',
        header: 'Images',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <PageNumbersEditor
            question={row.original}
            pastPaperId={paperId!}
            field="image_page_numbers"
          />
        ),
      },
      {
        id: 'mark_scheme',
        header: 'Mark Scheme',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => (
          <QuestionMarkSchemeEditor question={row.original} pastPaperId={paperId!} />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        size: 120,
        cell: ({ row }) => (
          <QuestionStatusSelect question={row.original} pastPaperId={paperId!} />
        ),
      },
    ],
    [paperId]
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return { month: '--', year: '--' };
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear().toString();
    return { month, year };
  };

  const { month, year } = formatDate(pastPaper?.date ?? null);

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="loading loading-spinner loading-lg text-[#C87941]"></div>
      </div>
    );
  }

  if (!pastPaper) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-gray-500">Past paper not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Header */}
      <PageHeader
        title=""
        showBackButton
        onBackClick={handleBack}
        rightContent={
          <div className="flex items-center gap-3">
            <UploadMarkSchemeButton pastPaperId={paperId!} pdfUrl={pastPaper.pdf_url || ''} />
            <LoadingButton
              loading={isPublishing}
              onClick={handlePublish}
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6"
            >
              Accept & Save
            </LoadingButton>
          </div>
        }
      />

      {/* Past Paper Info */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <div className="grid grid-cols-7 gap-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900 text-sm">
              {pastPaper.title || '--'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {month}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {year}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {pastPaper.subject || '--'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Paper Code</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900 font-mono">
              {pastPaper.paper_code || '--'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {pastPaper.time_allowed ? `${pastPaper.time_allowed} min` : '--'}
            </div>
          </div>
        </div>
        {/* Reading Insert Pages - always show, editable */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reading Insert Pages (Text 1)</label>
            <PastPaperReadingInsertEditor
              pastPaperId={paperId!}
              currentPages={pastPaper.reading_insert_page_numbers}
              field="reading_insert_page_numbers"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reading Insert Pages (Text 2)</label>
            <PastPaperReadingInsertEditor
              pastPaperId={paperId!}
              currentPages={pastPaper.reading_insert_page_numbers_2}
              field="reading_insert_page_numbers_2"
            />
          </div>
        </div>
      </div>

      {/* Questions Table */}
      <div className="bg-white rounded-3xl overflow-hidden shadow-sm mt-6">
        <DataTable
          data={pastPaper.questions}
          columns={columns}
          wrapperClassName="bg-white"
          headClassName="bg-gray-50 border-t border-gray-100"
          rowClassName="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          showPagination={false}
        />
      </div>
    </div>
  );
};

export default EditPastPaperPage;
